import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './WarehouseProductivity.css';
import { logout, getUserInfo, getAuthToken, filterWarehouseData } from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_URL = `${API_BASE}/warehouse`;

const orderTypeLabels = { A: 'Assemblies', C: 'Customer', T: 'Transfers' };

function WarehouseProductivityDispatched() {
  const navigate = useNavigate();
  const userInfo = getUserInfo();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [countries, setCountries] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [country, setCountry] = useState('All');
  const [orderType, setOrderType] = useState('All');
  const [excludeHQ, setExcludeHQ] = useState(false);
  const [excludeCarriers, setExcludeCarriers] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [cRes, otRes] = await Promise.all([
          axios.get(`${API_URL}/dispatched/countries`, { headers: headers() }),
          axios.get(`${API_URL}/dispatched/order-types`, { headers: headers() }),
        ]);
        setCountries(cRes.data.countries || []);
        setOrderTypes(otRes.data.orderTypes || []);
      } catch (err) {
        if (err.response?.status === 401) { logout(); window.location.href = '/login'; }
      }
    };
    loadFilters();
    fetchData('', '', 'All', 'All', false, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async (sd, ed, ctry, ot, exclHQ, excl) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (sd)   params.append('startDate', sd);
      if (ed)   params.append('endDate', ed);
      if (ctry && ctry !== 'All') params.append('country', ctry);
      if (ot   && ot   !== 'All') params.append('orderType', ot);
      if (exclHQ) params.append('excludeHQ', 'true');
      if (excl) params.append('excludeCarriers', 'true');

      const res = await axios.get(`${API_URL}/dispatched/summary?${params}`, { headers: headers() });
      setData(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) { logout(); window.location.href = '/login'; }
      else setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => fetchData(startDate, endDate, country, orderType, excludeHQ, excludeCarriers);

  const handleClear = () => {
    setStartDate(''); setEndDate('');
    setCountry('All'); setOrderType('All');
    setExcludeHQ(false); setExcludeCarriers(false);
    fetchData('', '', 'All', 'All', false, false);
  };

  const handleLogout = () => { logout(); window.location.href = '/login'; };

  const fmt = (n) => Number(n).toLocaleString('en-US');

  const warehouses = data ? filterWarehouseData(data.warehouses, 'warehouse') : [];

  const totals = warehouses.reduce((acc, w) => ({
    range_orders:        acc.range_orders        + w.range_orders,
    today_orders:        acc.today_orders        + w.today_orders,
    mtd_orders:          acc.mtd_orders          + w.mtd_orders,
  }), { range_orders: 0, today_orders: 0, mtd_orders: 0 });

  return (
    <div className="wp-container">
      <header className="wp-header">
        <div className="wp-header-top">
          <div>
            <h1>Warehouse Productivity</h1>
            <h2>Dispatched Orders — Invoiced Summary</h2>
          </div>
          <div className="wp-header-actions">
            {userInfo && <span className="wp-welcome">Welcome, {userInfo.fullName || userInfo.username}</span>}
            <button className="wp-back-btn" onClick={() => navigate('/')}>← Back to Home</button>
            <button className="wp-logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </div>
      </header>

      <div className="wp-filters">
        <div className="wp-filter-group">
          <label>Start Date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className="wp-filter-group">
          <label>End Date</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <div className="wp-filter-group">
          <label>Country</label>
          <select value={country} onChange={e => setCountry(e.target.value)}>
            <option value="All">All</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="wp-filter-group">
          <label>Order Type</label>
          <select value={orderType} onChange={e => setOrderType(e.target.value)}>
            <option value="All">All</option>
            {orderTypes.map(t => <option key={t} value={t}>{orderTypeLabels[t] || t}</option>)}
          </select>
        </div>
        <div className="wp-filter-group wp-checkbox-group">
          <label>
            <input type="checkbox" checked={excludeHQ} onChange={e => setExcludeHQ(e.target.checked)} />
            Exclude Customers: HQ
          </label>
        </div>
        <div className="wp-filter-group wp-checkbox-group">
          <label>
            <input type="checkbox" checked={excludeCarriers} onChange={e => setExcludeCarriers(e.target.checked)} />
            Exclude Carriers: <span className="carrier-tip" data-tip="Hold for Instructions">Z</span>
            {'/'}<span className="carrier-tip" data-tip="Will Call">WC</span>
            {'/'}<span className="carrier-tip" data-tip="Hold For Stock Truck">HFSK</span>
          </label>
        </div>
        <button className="wp-apply-btn" onClick={handleApply}>Apply Filters</button>
        <button className="wp-clear-btn" onClick={handleClear}>Clear Filters</button>
      </div>

      {loading && <div className="wp-loading">Loading...</div>}
      {error   && <div className="wp-error">{error}</div>}

      {!loading && data && (
        <div className="wp-content">
          <div className="wp-summary-bar">
            <span className="wp-date-range">{data.dateRange}</span>
            <div className="wp-totals">
              <span>All Warehouses — Invoiced Today: <strong>{fmt(totals.today_orders)}</strong></span>
              <span>MTD: <strong>{fmt(totals.mtd_orders)}</strong></span>
              <span>In Range: <strong>{fmt(totals.range_orders)}</strong></span>
            </div>
          </div>

          <div className="wp-grid">
            {warehouses.map(w => (
              <div key={w.warehouse} className="wp-card">
                <div className="wp-card-header">
                  <h3>{w.warehouse}</h3>
                </div>
                <div className="wp-metrics">
                  <div className="wp-metric">
                    <span className="wp-metric-label">Invoiced Today</span>
                    <span className="wp-metric-value">{fmt(w.today_orders)}</span>
                  </div>
                  <div className="wp-metric">
                    <span className="wp-metric-label">MTD Invoiced</span>
                    <span className="wp-metric-value">{fmt(w.mtd_orders)}</span>
                  </div>
                  <div className="wp-metric">
                    <span className="wp-metric-label">In Date Range</span>
                    <span className="wp-metric-value">{fmt(w.range_orders)}</span>
                  </div>
                  <div className="wp-metric">
                    <span className="wp-metric-label">Avg Days to Invoice</span>
                    <span className="wp-metric-value">{w.avg_days_to_invoice.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default WarehouseProductivityDispatched;
